/**
 * RevokedScreen - Displayed when a previously-approved user's access has
 * been revoked by an administrator.
 *
 * Distinct from RejectedScreen (pre-activation refusal):
 * - Acknowledges they were previously active
 * - Explains access can be reinstated
 * - Shows revocation reason if provided
 */

import { useEffect, useState } from 'react';
import { ShieldOff, LogOut, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface RevocationData {
  revocation_reason: string | null;
  revoked_at: string | null;
}

export default function RevokedScreen() {
  const { signOut, user } = useAuth();
  const [data, setData] = useState<RevocationData | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('profiles')
      .select('revocation_reason, revoked_at')
      .eq('user_id', user.id)
      .single()
      .then(({ data, error }) => {
        if (!error && data) setData(data);
      });
  }, [user?.id]);

  const formatDate = (s: string | null) =>
    s ? new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy via-navy/95 to-steel flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="flex justify-center mb-8">
          <img
            src="/images/wingmentor-logo.png"
            alt="WingMentor Logo"
            className="h-20 w-auto"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </div>

        <div className="bg-white rounded-xl shadow-2xl p-8 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
            <ShieldOff size={40} className="text-amber-600" />
          </div>

          <h1 className="text-2xl font-bold text-navy mb-2">Access Revoked</h1>
          <p className="text-steel mb-6">
            Your access to WingMentor has been revoked by an administrator.
            Your profile and training history are preserved and access can be reinstated.
          </p>

          {data?.revocation_reason && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800 mb-1">Reason provided:</p>
                  <p className="text-sm text-amber-700">{data.revocation_reason}</p>
                </div>
              </div>
            </div>
          )}

          {data?.revoked_at && (
            <p className="text-sm text-gray-500 mb-6">
              Revoked on {formatDate(data.revoked_at)}
            </p>
          )}

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-700">
              To request reinstatement, contact your organisation administrator
              or WingMentor support.
            </p>
          </div>

          <button
            onClick={() => signOut()}
            className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>

        <p className="text-center text-white/60 text-sm mt-6">
          WingMentor — Competency Assessment Platform
        </p>
      </div>
    </div>
  );
}
